import styles from "./UnrecognizedEmailAppPrompt.module.scss";

import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";

import { env } from "@/config/env";
import { AppStoreButton } from "@/modules/shared/components/AppStoreButton";
import { GooglePlayButton } from "@/modules/shared/components/GooglePlayButton";
import { ROUTES } from "@/modules/shared/consts/routes";
import { useIsMobile } from "@/modules/shared/hooks/useIsMobile";
import { captureEvent } from "@/modules/shared/utils/posthog";
import { COACH_MATCHING_PAGE_EVENTS } from "@/modules/tracking/consts/Events";

type AppDownloadTarget = "smart_link" | "app_store" | "google_play";

const trackDownloadTap = (target: AppDownloadTarget) => {
  captureEvent(COACH_MATCHING_PAGE_EVENTS.APP_DOWNLOAD_TAPPED, { target });
};
const handleSmartLinkClick = () => trackDownloadTap("smart_link");
const handleAppStoreClick = () => trackDownloadTap("app_store");
const handleGooglePlayClick = () => trackDownloadTap("google_play");

interface UnrecognizedEmailAppPromptProps {
  supportRoute?: string;
}

/**
 * Shown under the email field when the public Coach Matching page can't find
 * the member's email. Members without an account sign up in the app, so phones
 * get the smart link (app if installed, store if not) and computers get a QR.
 */
export function UnrecognizedEmailAppPrompt({
  supportRoute = ROUTES.support.index,
}: UnrecognizedEmailAppPromptProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile(true);

  return (
    <div className={styles.container}>
      <p className={styles.newToSharp}>
        {t("book.confirmDetails.coachMatching.appPrompt.newToSharp")}
      </p>
      {isMobile ? (
        <a
          href={env.VITE_CALADAN_COACH_MATCHING_URL}
          className={styles.getAppLink}
          onClick={handleSmartLinkClick}
        >
          {t("book.confirmDetails.coachMatching.appPrompt.getApp")}
        </a>
      ) : (
        <div className={styles.desktopDownload}>
          <img
            src="/qr.png"
            width={140}
            height={140}
            alt={t("mobileRequired.qrCodeAltText")}
            className={styles.qrCode}
          />
          <p className={styles.scanHint}>
            {t("book.confirmDetails.coachMatching.appPrompt.scanHint")}
          </p>
          <div className={styles.storeButtons}>
            <AppStoreButton onClick={handleAppStoreClick} />
            <GooglePlayButton onClick={handleGooglePlayClick} />
          </div>
        </div>
      )}
      <p className={styles.supportCopy}>
        <Trans
          i18nKey="book.confirmDetails.coachMatching.appPrompt.support"
          components={{
            supportLink: (
              <Link to={supportRoute} className={styles.supportLink} />
            ),
          }}
        />
      </p>
    </div>
  );
}
